/**
 * QuickEnrolWizard — modal opened from the Setup Checklist that lists all
 * active students who don't currently have a class enrolment and lets the
 * admin bulk-enrol them into one class in 3 clicks.
 *
 * Backend: GET /api/school/tenants/{tid}/students/unenrolled
 *          POST /api/school/tenants/{tid}/enrollments/bulk
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, Search, GraduationCap, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function jget(path) {
  const res = await fetch(`${API_URL}${path}`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Request failed');
  return res.json();
}

async function jpost(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Request failed');
  return res.json();
}

export default function QuickEnrolWizard({ open, onClose, tenantId }) {
  const qc = useQueryClient();
  const [classId, setClassId] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);

  const { data: students = [], isLoading } = useQuery({
    queryKey: ['unenrolled-students', tenantId],
    queryFn: () => jget(`/api/school/tenants/${tenantId}/students/unenrolled`),
    enabled: open && !!tenantId,
    refetchOnWindowFocus: false,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => jget(`/api/school/tenants/${tenantId}/classes`),
    enabled: open && !!tenantId,
  });

  React.useEffect(() => {
    if (open) { setSelected({}); setSearch(''); setResult(null); setClassId(''); }
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return students;
    const q = search.toLowerCase();
    return students.filter(s =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
      (s.admission_number || '').toLowerCase().includes(q)
    );
  }, [students, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(s => selected[s.id]);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleOne = (id) => setSelected(p => ({ ...p, [id]: !p[id] }));
  const toggleAll = () => {
    if (allFilteredSelected) {
      const next = { ...selected };
      filtered.forEach(s => { next[s.id] = false; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filtered.forEach(s => { next[s.id] = true; });
      setSelected(next);
    }
  };

  const enrollMutation = useMutation({
    mutationFn: () => jpost(`/api/school/tenants/${tenantId}/enrollments/bulk`, {
      class_id: classId,
      student_ids: Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    }),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`Enrolled ${r.enrolled} student(s)`);
      qc.invalidateQueries({ queryKey: ['unenrolled-students', tenantId] });
      qc.invalidateQueries({ queryKey: ['school-setup-status', tenantId] });
      qc.invalidateQueries({ queryKey: ['school-students-list', tenantId] });
      qc.invalidateQueries({ queryKey: ['school-dashboard', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const submit = () => {
    if (!classId) { toast.error('Pick a class first'); return; }
    if (selectedCount === 0) { toast.error('Select at least one student'); return; }
    enrollMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="quick-enrol-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" /> Quick-enrol students
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="py-6 text-center space-y-3" data-testid="quick-enrol-result">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold">Enrolled {result.enrolled} student(s)</h3>
            <div className="text-sm text-slate-500 space-y-1">
              {result.skipped_over_capacity > 0 && (
                <p className="flex items-center justify-center gap-1 text-amber-700">
                  <AlertTriangle className="h-3 w-3" />
                  {result.skipped_over_capacity} skipped — class was full
                </p>
              )}
              <p>Class remaining capacity: {result.class_remaining} of {result.class_capacity}</p>
            </div>
            <Button onClick={() => onClose && onClose()} data-testid="quick-enrol-done">Done</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Class to enrol into</Label>
                <Select value={classId} onValueChange={setClassId}>
                  <SelectTrigger data-testid="quick-enrol-class-select">
                    <SelectValue placeholder="Choose a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <GraduationCap className="h-3.5 w-3.5" />
                          {c.name}
                          <span className="text-xs text-slate-400">({c.student_count || 0}/{c.capacity || 40})</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Search students</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
                  <Input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Name or admission number"
                    className="pl-8"
                    data-testid="quick-enrol-search"
                  />
                </div>
              </div>
            </div>

            <div className="border rounded-md">
              <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between text-xs">
                <span className="text-slate-600">
                  {isLoading ? 'Loading…' : `${filtered.length} unenrolled student(s)`}
                  <Badge variant="outline" className="ml-2 bg-indigo-50 text-indigo-700">
                    {selectedCount} selected
                  </Badge>
                </span>
                {filtered.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-indigo-600 hover:underline"
                    data-testid="quick-enrol-toggle-all"
                  >
                    {allFilteredSelected ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </div>
              <div className="max-h-72 overflow-auto">
                {isLoading ? (
                  <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
                ) : filtered.length === 0 ? (
                  <div className="py-10 text-center text-sm text-slate-400">
                    {students.length === 0 ? 'Every student is already enrolled. Nothing to do.' : 'No students match your search.'}
                  </div>
                ) : (
                  filtered.map(s => (
                    <label
                      key={s.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                      data-testid={`quick-enrol-row-${s.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[s.id]}
                        onChange={() => toggleOne(s.id)}
                        data-testid={`quick-enrol-cb-${s.id}`}
                      />
                      <span className="flex-1">
                        <span className="font-medium">{s.first_name} {s.last_name}</span>
                        <span className="text-xs text-slate-400 ml-2">{s.admission_number}</span>
                      </span>
                      {s.gender && (
                        <span className="text-xs text-slate-400 capitalize">{s.gender}</span>
                      )}
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {!result && (
          <DialogFooter className="mt-2">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={submit}
              disabled={enrollMutation.isPending || !classId || selectedCount === 0}
              data-testid="quick-enrol-submit"
            >
              {enrollMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Enrol {selectedCount > 0 ? `${selectedCount} ` : ''}student{selectedCount === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
